import {
  createContext,
  useState,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";
import {
  $getRoot,
  $isTextNode,
  TextNode,
  LexicalNode,
  $getNodeByKey,
  $isElementNode,
  $createTextNode,
  $getSelection,
  $setSelection,
  $createRangeSelection,
  $createPoint,
  COMMAND_PRIORITY_LOW,
  createCommand,
  LexicalCommand,
} from "lexical";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createSearchHighlightNode,
  $isSearchHighlightNode,
  SearchHighlightNode,
} from "../nodes/SearchHighlightNode";

// Import CSS directly
import "../search-highlight.css";

export interface MatchInfo {
  id: string;
  nodeKey: string;
  start: number;
  end: number;
  text: string;
  element?: HTMLElement;
}

export const OPEN_FIND_REPLACE_COMMAND: LexicalCommand<void> = createCommand();
export const CLOSE_FIND_REPLACE_COMMAND: LexicalCommand<void> = createCommand();

interface FindReplaceContextType {
  searchTerm: string;
  replaceTerm: string;
  caseSensitive: boolean;
  wholeWord: boolean;
  setSearchTerm: (s: string) => void;
  setReplaceTerm: (s: string) => void;
  setCaseSensitive: (c: boolean) => void;
  setWholeWord: (w: boolean) => void;
  findMatches: () => void;
  findNext: () => void;
  findPrevious: () => void;
  replaceCurrent: () => void;
  replaceAll: () => void;
  clearHighlights: () => void;
  matches: MatchInfo[];
  currentIndex: number;
  setModalOpen: (open: boolean) => void;
  modalOpen: boolean;
  isSearching: boolean;
}

export const FindReplaceContext = createContext<FindReplaceContextType | null>(
  null
);

export const useFindReplace = () => {
  const ctx = useContext(FindReplaceContext);
  if (!ctx) throw new Error("FindReplaceContext is missing");
  return ctx;
};

export const FindReplaceProvider = ({ children }: { children: ReactNode }) => {
  const [editor] = useLexicalComposerContext();
  const [searchTerm, setSearchTerm] = useState("");
  const [replaceTerm, setReplaceTerm] = useState("");
  const [caseSensitive, setCaseSensitive] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [matches, setMatches] = useState<MatchInfo[]>([]);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [modalOpen, setModalOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Register commands
  useEffect(() => {
    const unregisterOpen = editor.registerCommand(
      OPEN_FIND_REPLACE_COMMAND,
      () => {
        setModalOpen(true);
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const unregisterClose = editor.registerCommand(
      CLOSE_FIND_REPLACE_COMMAND,
      () => {
        setModalOpen(false);
        clearHighlights();
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      unregisterOpen();
      unregisterClose();
    };
  }, [editor]);

  const getAllTextNodes = useCallback((): TextNode[] => {
    const nodes: TextNode[] = [];

    function traverse(node: LexicalNode) {
      if ($isTextNode(node) && !$isSearchHighlightNode(node)) {
        nodes.push(node);
      } else if ($isElementNode(node)) {
        for (const child of node.getChildren()) {
          traverse(child);
        }
      }
    }

    traverse($getRoot());
    return nodes;
  }, []);

  const escapeRegExp = (string: string): string => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  };

  const createSearchRegex = useCallback(
    (term: string): RegExp => {
      let pattern = escapeRegExp(term);

      if (wholeWord) {
        pattern = `\\b${pattern}\\b`;
      }

      const flags = caseSensitive ? "g" : "gi";
      return new RegExp(pattern, flags);
    },
    [caseSensitive, wholeWord]
  );

  const clearHighlights = useCallback(() => {
    editor.update(() => {
      const root = $getRoot();

      function removeHighlights(node: LexicalNode) {
        if ($isSearchHighlightNode(node)) {
          // Extract the original text content and restore any preserved formatting
          const originalText = node.getTextContent();
          const textNode = $createTextNode(originalText);

          // Restore original formatting if it was preserved
          const originalFormat = node.getOriginalFormat();
          const originalStyle = node.getOriginalStyle();

          if (originalFormat !== undefined) {
            textNode.setFormat(originalFormat);
          }
          if (originalStyle !== undefined) {
            textNode.setStyle(originalStyle);
          }

          node.replace(textNode);
        } else if ($isElementNode(node)) {
          const children = [...node.getChildren()]; // Create copy to avoid mutation
          for (const child of children) {
            removeHighlights(child);
          }
        }
      }

      removeHighlights(root);
    });

    setMatches([]);
    setCurrentIndex(-1);
  }, [editor]);

  const scrollToMatch = useCallback((matchId: string) => {
    // Small delay to ensure DOM is updated
    setTimeout(() => {
      const element = document.querySelector(
        `[data-match-id="${matchId}"]`
      ) as HTMLElement;
      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);
  }, []);

  const findMatches = useCallback(() => {
    console.log("🔍 findMatches called with searchTerm:", searchTerm);

    if (!searchTerm.trim()) {
      clearHighlights();
      return;
    }

    setIsSearching(true);

    editor.update(() => {
      console.log("📝 Inside editor.update");

      // Clear existing highlights first
      const root = $getRoot();
      function removeHighlights(node: LexicalNode) {
        if ($isSearchHighlightNode(node)) {
          const originalText = node.getTextContent();
          const textNode = $createTextNode(originalText);
          node.replace(textNode);
        } else if ($isElementNode(node)) {
          const children = [...node.getChildren()];
          for (const child of children) {
            removeHighlights(child);
          }
        }
      }
      removeHighlights(root);

      const regex = createSearchRegex(searchTerm.trim());
      const textNodes = getAllTextNodes();
      console.log("📄 Found text nodes:", textNodes.length);

      const newMatches: MatchInfo[] = [];
      let matchCounter = 0;

      // Process each text node and split it only if it contains matches
      textNodes.forEach((originalNode) => {
        const text = originalNode.getTextContent();
        console.log("🔤 Processing text node:", text);

        const matchResults = Array.from(text.matchAll(regex));
        console.log("🎯 Matches found:", matchResults.length);

        if (matchResults.length === 0) return;

        // Store original node formatting for preservation
        const originalFormat = originalNode.getFormat();
        const originalStyle = originalNode.getStyle();
        const originalMode = originalNode.getMode();

        let offset = 0;
        const newNodes: LexicalNode[] = [];

        matchResults.forEach((match) => {
          const matchStart = match.index!;
          const matchEnd = matchStart + match[0].length;
          const matchId = `match-${matchCounter++}`;

          console.log(
            "✨ Creating highlight for:",
            match[0],
            "at",
            matchStart,
            "-",
            matchEnd
          );

          // Add text before match (preserve original formatting)
          if (matchStart > offset) {
            const beforeText = text.slice(offset, matchStart);
            const beforeNode = $createTextNode(beforeText);
            beforeNode.setFormat(originalFormat);
            beforeNode.setStyle(originalStyle);
            beforeNode.setMode(originalMode);
            newNodes.push(beforeNode);
          }

          // Add highlighted match - preserve original formatting in the highlight node
          const highlightNode = $createSearchHighlightNode(
            match[0],
            false, // Not active initially
            matchId,
            originalFormat,
            originalStyle
          );
          newNodes.push(highlightNode);

          // Store match info
          newMatches.push({
            id: matchId,
            nodeKey: originalNode.getKey(),
            start: matchStart,
            end: matchEnd,
            text: match[0],
          });

          offset = matchEnd;
        });

        // Add remaining text (preserve original formatting)
        if (offset < text.length) {
          const remainingText = text.slice(offset);
          const remainingNode = $createTextNode(remainingText);
          remainingNode.setFormat(originalFormat);
          remainingNode.setStyle(originalStyle);
          remainingNode.setMode(originalMode);
          newNodes.push(remainingNode);
        }

        // Replace the original node with new nodes only if we have matches
        if (newNodes.length > 0) {
          console.log("🔄 Replacing node with", newNodes.length, "new nodes");
          originalNode.replace(newNodes[0]);
          for (let i = 1; i < newNodes.length; i++) {
            newNodes[i - 1].insertAfter(newNodes[i]);
          }
        }
      });

      console.log("📊 Total matches found:", newMatches.length);
      setMatches(newMatches);

      // Set first match as active if any matches found
      if (newMatches.length > 0) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex(-1);
      }
    });

    setIsSearching(false);
  }, [editor, searchTerm, createSearchRegex, getAllTextNodes, clearHighlights]);

  // Auto-search when term changes with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.trim() && modalOpen) {
      searchTimeoutRef.current = setTimeout(() => {
        findMatches();
      }, 300);
    } else {
      clearHighlights();
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [
    searchTerm,
    caseSensitive,
    wholeWord,
    modalOpen,
    findMatches,
    clearHighlights,
  ]);

  // Update active match highlighting
  useEffect(() => {
    if (matches.length === 0 || currentIndex === -1) return;

    editor.update(() => {
      // Clear all active states
      matches.forEach((match) => {
        const elements = document.querySelectorAll(
          `[data-match-id="${match.id}"]`
        );
        elements.forEach((el: Element) => {
          const htmlEl = el as HTMLElement;
          htmlEl.className = "search-highlight";
        });
      });

      // Set current match as active
      if (currentIndex >= 0 && currentIndex < matches.length) {
        const currentMatch = matches[currentIndex];
        const currentElements = document.querySelectorAll(
          `[data-match-id="${currentMatch.id}"]`
        );
        currentElements.forEach((el: Element) => {
          const htmlEl = el as HTMLElement;
          htmlEl.className = "search-highlight-active";
        });

        // Scroll to active match
        scrollToMatch(currentMatch.id);
      }
    });
  }, [editor, matches, currentIndex, scrollToMatch]);

  const findNext = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentIndex((prev: number) => (prev + 1) % matches.length);
  }, [matches.length]);

  const findPrevious = useCallback(() => {
    if (matches.length === 0) return;
    setCurrentIndex(
      (prev: number) => (prev - 1 + matches.length) % matches.length
    );
  }, [matches.length]);

  const replaceCurrent = useCallback(() => {
    if (matches.length === 0 || currentIndex === -1) return;

    const currentMatch = matches[currentIndex];

    editor.update(() => {
      const root = $getRoot();

      function findAndReplaceHighlight(node: LexicalNode): boolean {
        if (
          $isSearchHighlightNode(node) &&
          node.getMatchId() === currentMatch.id
        ) {
          // Create replacement text node and preserve original formatting
          const textNode = $createTextNode(replaceTerm);

          // Restore original formatting if it was preserved
          const originalFormat = node.getOriginalFormat();
          const originalStyle = node.getOriginalStyle();

          if (originalFormat !== undefined) {
            textNode.setFormat(originalFormat);
          }
          if (originalStyle !== undefined) {
            textNode.setStyle(originalStyle);
          }

          node.replace(textNode);
          return true;
        } else if ($isElementNode(node)) {
          for (const child of node.getChildren()) {
            if (findAndReplaceHighlight(child)) {
              return true;
            }
          }
        }
        return false;
      }

      findAndReplaceHighlight(root);
    });

    // Re-run search to update matches
    setTimeout(() => findMatches(), 100);
  }, [editor, matches, currentIndex, replaceTerm, findMatches]);

  const replaceAll = useCallback(() => {
    if (matches.length === 0) return;

    editor.update(() => {
      const root = $getRoot();

      function replaceAllHighlights(node: LexicalNode) {
        if ($isSearchHighlightNode(node)) {
          // Create replacement text node and preserve original formatting
          const textNode = $createTextNode(replaceTerm);

          // Restore original formatting if it was preserved
          const originalFormat = node.getOriginalFormat();
          const originalStyle = node.getOriginalStyle();

          if (originalFormat !== undefined) {
            textNode.setFormat(originalFormat);
          }
          if (originalStyle !== undefined) {
            textNode.setStyle(originalStyle);
          }

          node.replace(textNode);
        } else if ($isElementNode(node)) {
          const children = [...node.getChildren()]; // Create copy to avoid mutation during iteration
          for (const child of children) {
            replaceAllHighlights(child);
          }
        }
      }

      replaceAllHighlights(root);
    });

    // Clear matches and close modal
    setMatches([]);
    setCurrentIndex(-1);
    setSearchTerm("");
  }, [editor, replaceTerm]);

  const contextValue: FindReplaceContextType = {
    searchTerm,
    replaceTerm,
    caseSensitive,
    wholeWord,
    setSearchTerm,
    setReplaceTerm,
    setCaseSensitive,
    setWholeWord,
    findMatches,
    findNext,
    findPrevious,
    replaceCurrent,
    replaceAll,
    clearHighlights,
    matches,
    currentIndex,
    modalOpen,
    setModalOpen,
    isSearching,
  };

  return (
    <FindReplaceContext.Provider value={contextValue}>
      {children}
    </FindReplaceContext.Provider>
  );
};
